import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import Sidebar from "../../components/Sidebar/Sidebar";
import Header from "../../components/Header/Header";
import { useConfirm } from "../../components/ConfirmDialog/ConfirmDialog";

import {
  createStaff,
  getRoles,
  updateStaff,
  getStaffById,
  getBranches,
  getServiceLocations,
  uploadStaffPhoto,
  sendStaffOtp,
  verifyStaffOtp,
} from "../../services/staff_service";

import "./AddStaff.css";

const API_BASE_URL = "https://srinivasafleet.com/api";

const resolvePhotoUrl = (path) => {
  if (!path) return "";
  if (path.startsWith("http")) return path;
  return `${API_BASE_URL}${path}`;
};

const STATUS_OPTIONS = [
  "Active",
  "Deactive",
  "Block Listed",
];

const DESIGNATIONS = [
  "M.D",
  "V.P",
  "AVP",
  "GM",
  "AGM",
  "Sr. Manager",
  "Manager",
  "Asst. Manager",
  "Sr. Executive",
  "Executive",
];

const MODULE_PERMISSION_FIELDS = [
  "dashboard",
  "drivers",
  "vehicles",
  "vehicle_documents",
  "live_tracking",
  "branch_overview",
  "suppliers",
  "assignments",
  "followups",
  "outstanding_view",
  "outstanding_update",
  "reports",
  "settings",
  "master_data",
  "auditlog",
  "recycle_bin",
  "staff_management",
  "messages",
];

const ACCESS_LEVELS = ["view", "edit", "delete"];

const DEFAULT_PERMISSIONS = MODULE_PERMISSION_FIELDS.reduce(
  (result, key) => {
    result[key] = key === "dashboard";
    return result;
  },
  {}
);

const PERMISSION_LABELS = {
  dashboard: "Dashboard",
  drivers: "Drivers",
  vehicles: "Vehicles",
  vehicle_documents: "Vehicle Documents",
  live_tracking: "Live Tracking",
  branch_overview: "Branch Overview",
  suppliers: "Suppliers",
  assignments: "Vehicle Allotment",
  followups: "Follow-ups",
  outstanding_view: "Outstanding View",
  outstanding_update: "Outstanding Update",
  reports: "Reports",
  settings: "Settings",
  master_data: "Master Data",
  auditlog: "Audit",
  recycle_bin: "Recycle Bin",
  staff_management: "Staff Management",
  messages: "Messages",
};

export default function AddStaff() {
  const navigate = useNavigate();
  const { id } = useParams();
  const confirm = useConfirm();

  const isEdit = Boolean(id);

  const getCurrentRoleName = () => {
    const stored =
      localStorage.getItem("role_name") ||
      localStorage.getItem("role") ||
      localStorage.getItem("user_role") ||
      "";

    if (stored) {
      return String(stored)
        .trim()
        .toLowerCase()
        .replace(/[_-]+/g, " ")
        .replace(/\s+/g, " ");
    }

    const token =
      localStorage.getItem("token") ||
      localStorage.getItem("access_token") ||
      "";

    if (!token) return "";

    try {
      const parts = token.split(".");
      if (parts.length !== 3) return "";

      const base64 = parts[1]
        .replace(/-/g, "+")
        .replace(/_/g, "/");

      const padded = base64.padEnd(
        base64.length + ((4 - base64.length % 4) % 4),
        "="
      );

      const decoded = JSON.parse(atob(padded));

      return String(
        decoded.role_name ||
        decoded.role ||
        ""
      )
        .trim()
        .toLowerCase()
        .replace(/[_-]+/g, " ")
        .replace(/\s+/g, " ");
    } catch {
      return "";
    }
  };

  const currentRoleName = getCurrentRoleName();
  const isSuperadmin = currentRoleName === "superadmin";
  const isAdmin = currentRoleName === "admin";

  const [roles, setRoles] = useState([]);
  const [branches, setBranches] = useState([]);
  const [serviceLocations, setServiceLocations] = useState([]);

  const [loading, setLoading] = useState(false);

  const [, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState("");
  const [photoUploading, setPhotoUploading] = useState(false);

  const [staff, setStaff] = useState({
    full_name: "",
    email: "",
    mobile: "",

    password: "",
    confirm_password: "",

    role_id: "",

    designation: "",

    service_location_id: "",
    branch_id: "",

    address: "",
    city: "",
    pincode: "",

    status: "Active",
    is_active: true,

    photo_url: "",

    permissions: {
      ...DEFAULT_PERMISSIONS,
    },
  });

  const [staffCode, setStaffCode] = useState("");
  const [permissionLevels, setPermissionLevels] = useState(["view"]);
  const [originalMobile, setOriginalMobile] = useState("");
  const [otp, setOtp] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [otpVerified, setOtpVerified] = useState(false);
  const [verificationId, setVerificationId] = useState("");
  const [sendingOtp, setSendingOtp] = useState(false);
  const [verifyingOtp, setVerifyingOtp] = useState(false);
  const [otpModalOpen, setOtpModalOpen] = useState(false);

  useEffect(() => {
    fetchRoles();
    fetchBranches();
    fetchServiceLocations();

    if (id) {
      fetchStaffById();
    }
  }, [id]);

  const fetchRoles = async () => {
    try {
      const response = await getRoles();
      const allRoles = Array.isArray(response) ? response : [];

      const visibleRoles = allRoles.filter((role) => {
        const name = String(role.role_name || "").trim().toLowerCase();
        if (isSuperadmin) return true;
        if (isAdmin) return name === "staff";

        return false;
      });

      setRoles(visibleRoles);

      if (!id && visibleRoles.length) {
        const staffRole = visibleRoles.find(
          (role) => String(role.role_name || "").trim().toLowerCase() === "staff"
        );

        setStaff((prev) => ({
          ...prev,
          role_id: staffRole?.id || visibleRoles[0].id,
        }));
      }
    } catch (err) {
      console.error("Failed to fetch roles:", err);
    }
  };

  const fetchBranches = async () => {
    try {
      const data = await getBranches();

      setBranches(data || []);
    } catch (err) {
      console.error("Failed to fetch branches:", err);
    }
  };

  const fetchServiceLocations = async () => {
    try {
      const data = await getServiceLocations();

      setServiceLocations(data || []);

      if (
        data &&
        data.length === 1 &&
        !id
      ) {
        setStaff((prev) => ({
          ...prev,
          service_location_id: data[0].id,
        }));
      }
    } catch (err) {
      console.error(
        "Failed to fetch service locations:",
        err
      );
    }
  };

  const fetchStaffById = async () => {
    try {
      const data = await getStaffById(id);

      const loadedPermissions = MODULE_PERMISSION_FIELDS.reduce(
        (result, key) => {
          result[key] = Boolean(data.permissions?.[key]);
          return result;
        },
        { ...DEFAULT_PERMISSIONS }
      );

      const loadedLevels = Array.isArray(data.permissions?.permission_levels)
        ? data.permissions.permission_levels
        : String(data.permissions?.permission_level || "view")
            .split(",")
            .map((item) => item.trim().toLowerCase())
            .filter((item) => ACCESS_LEVELS.includes(item));

      setStaff({
        full_name: data.full_name || "",
        email: data.email || "",
        mobile: data.mobile || "",
        password: "",
        confirm_password: "",
        role_id: data.role_id || "",
        designation: data.designation || "",
        service_location_id:
          data.service_location_id || "",
        branch_id:
          data.branch_id || "",
        address: data.address || "",
        city: data.city || "",
        pincode: data.pincode || "",
        status: data.status || "Active",
        is_active: data.is_active ?? true,
        photo_url: data.photo_url || "",
        permissions: loadedPermissions,
      });

      setStaffCode(
        data.staff_code || ""
      );
      setOriginalMobile(data.mobile || "");
      setOtpVerified(true);
      setOtpSent(false);
      setOtp("");
      setVerificationId("");

      setPermissionLevels(
        Array.from(new Set(loadedLevels.filter((level) => ACCESS_LEVELS.includes(level))))
      );

      if (data.photo_url) {
        setPhotoPreview(
          resolvePhotoUrl(data.photo_url)
        );
      }
    } catch (err) {
      console.error(
        "Failed to fetch staff:",
        err
      );

      alert("Unable to fetch staff");
    }
  };

  const handleChange = (e) => {
    const {
      name,
      value,
      checked,
      type,
    } = e.target;

    if (
      type === "checkbox" &&
      Object.prototype.hasOwnProperty.call(
        staff.permissions,
        name
      )
    ) {
      setStaff((prev) => ({
        ...prev,

        permissions: {
          ...prev.permissions,
          [name]: checked,
        },
      }));

      return;
    }

    if (type === "checkbox") {
      setStaff((prev) => ({
        ...prev,
        [name]: checked,
      }));

      return;
    }
    if (name === "mobile") {
      const normalizedMobile = String(value || "").replace(/\D/g, "").slice(0, 10);

      setStaff((prev) => ({
        ...prev,
        mobile: normalizedMobile,
      }));

      const original = String(originalMobile || "").replace(/\D/g, "");

      if (!isEdit || normalizedMobile !== original) {
        setOtp("");
        setOtpSent(false);
        setOtpVerified(false);
        setVerificationId("");
        setOtpModalOpen(false);
      }

      return;
    }

    setStaff((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSendOtp = async () => {
    const mobile = String(staff.mobile || "").replace(/\D/g, "");

    if (!/^[6-9]\d{9}$/.test(mobile)) {
      alert("Please enter a valid 10-digit mobile number");
      return;
    }

    if (isEdit && mobile === String(originalMobile || "").replace(/\D/g, "")) {
      setOtpVerified(true);
      return;
    }

    try {
      setSendingOtp(true);
      const result = await sendStaffOtp(mobile);
      setVerificationId(result?.verification_id || "");
      setOtpSent(true);
      setOtpVerified(false);
      setOtp("");
      setOtpModalOpen(true);
    } catch (err) {
      console.error("Send staff OTP error:", err);
      alert(err?.response?.data?.detail || "Unable to send OTP");
    } finally {
      setSendingOtp(false);
    }
  };

  const handleVerifyOtp = async () => {
    const mobile = String(staff.mobile || "").replace(/\D/g, "");

    if (!verificationId) {
      alert("Please send OTP first");
      return;
    }

    if (!/^\d{6}$/.test(otp)) {
      alert("Please enter the 6-digit OTP");
      return;
    }

    try {
      setVerifyingOtp(true);
      const result = await verifyStaffOtp(mobile, otp, verificationId);
      setOtpVerified(Boolean(result?.verified));
      if (result?.verified) {
        setOtpModalOpen(false);
      } else {
        alert("OTP verification failed");
      }
    } catch (err) {
      console.error("Verify staff OTP error:", err);
      setOtpVerified(false);
      alert(err?.response?.data?.detail || "Invalid or expired OTP");
    } finally {
      setVerifyingOtp(false);
    }
  };

  const handlePhotoChange = async (e) => {
    const file = e.target.files?.[0];

    if (!file) {
      return;
    }

    setPhotoFile(file);

    setPhotoPreview(
      URL.createObjectURL(file)
    );

    setPhotoUploading(true);

    try {
      const result =
        await uploadStaffPhoto(file);

      setStaff((prev) => ({
        ...prev,
        photo_url:
          result.url || "",
      }));

      setPhotoPreview(
        resolvePhotoUrl(result.url)
      );
    } catch (err) {
      console.error(
        "Photo upload failed:",
        err
      );

      alert(
        "Failed to upload photo. Please try again."
      );

      setPhotoPreview(
        resolvePhotoUrl(
          staff.photo_url
        ) || ""
      );
    } finally {
      setPhotoUploading(false);
    }
  };

  const handleBack = async () => {
    const confirmed = await confirm({
      title: "Unsaved Changes",
      message:
        "Are you sure you want to go back? Any unsaved changes will be lost.",
      confirmLabel: "Go Back",
      cancelLabel: "Stay on Page",
      danger: true,
    });

    if (confirmed) {
      navigate("/staff");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!isEdit && !otpVerified) {
      alert("Please verify the mobile number with OTP before creating staff");
      return;
    }

    if (isEdit && String(staff.mobile || "").replace(/\D/g, "") !== String(originalMobile || "").replace(/\D/g, "") && !otpVerified) {
      alert("Please verify the new mobile number with OTP before updating staff");
      return;
    }

    if (
      !isEdit &&
      staff.password !==
        staff.confirm_password
    ) {
      alert("Passwords do not match");
      return;
    }

    if (photoUploading) {
      alert(
        "Please wait for the photo to finish uploading."
      );

      return;
    }

    try {
      setLoading(true);

      const selectedRole = roles.find(
        (role) => String(role.id) === String(staff.role_id)
      );

      const selectedRoleName = String(
        selectedRole?.role_name || ""
      ).trim().toLowerCase();

      const permissions = MODULE_PERMISSION_FIELDS.reduce(
        (result, key) => {
          result[key] = Boolean(staff.permissions?.[key]);
          return result;
        },
        {}
      );

      let savedPermissionLevels = ["view"];

      if (isSuperadmin) {
        savedPermissionLevels = permissionLevels.filter((level) =>
          ACCESS_LEVELS.includes(level)
        );
        if (!savedPermissionLevels.length) {
          savedPermissionLevels = ["view"];
        }
      } else if (isAdmin) {
        savedPermissionLevels = ["view"];
      } else {
        MODULE_PERMISSION_FIELDS.forEach((key) => {
          permissions[key] = false;
        });
      }

      permissions.permission_levels = savedPermissionLevels;
      permissions.permission_level = savedPermissionLevels.join(",");

      const payload = {
        full_name:
          staff.full_name.trim(),

        email:
          staff.email.trim() || null,

        mobile:
          staff.mobile.trim(),

        role_id:
          Number(staff.role_id),

        designation:
          staff.designation || null,

        service_location_id:
          staff.service_location_id
            ? Number(
                staff.service_location_id
              )
            : null,

        branch_id:
          staff.branch_id
            ? Number(staff.branch_id)
            : null,

        address:
          staff.address.trim() || null,

        city:
          staff.city.trim() || null,

        pincode:
          staff.pincode.trim() || null,

        status:
          staff.status,

        is_active:
          staff.status === "Active",

        photo_url:
          staff.photo_url || null,

        permissions,
      };

      if (!isEdit) {
        payload.password =
          staff.password;

        payload.confirm_password =
          staff.confirm_password;

        payload.mobile_verification_id = verificationId;
        await createStaff(payload);

        alert(
          "Staff Created Successfully"
        );
      }

      else {
        if (String(staff.mobile || "").replace(/\D/g, "") !== String(originalMobile || "").replace(/\D/g, "")) {
          payload.mobile_verification_id = verificationId;
        }
        await updateStaff(
          id,
          payload
        );

        alert(
          "Staff Updated Successfully"
        );
      }

      navigate("/staff");
    } catch (err) {
      console.error(
        "Staff save error:",
        err
      );

      alert(
        err?.response?.data?.detail ||
          "Unable to save staff"
      );
    } finally {
      setLoading(false);
    }
  };

  const selectedTargetRole = roles.find(
    (role) => String(role.id) === String(staff.role_id)
  );

  const selectedTargetRoleName = String(
    selectedTargetRole?.role_name || ""
  ).trim().toLowerCase();

  const handleSelectAllPermissions = () => {
    setStaff((prev) => ({
      ...prev,
      permissions: MODULE_PERMISSION_FIELDS.reduce(
        (result, key) => {
          result[key] = true;
          return result;
        },
        {}
      ),
    }));

    setPermissionLevels(isSuperadmin ? [...ACCESS_LEVELS] : ["view"]);
  };

  const handleClearAllPermissions = () => {
    setStaff((prev) => ({
      ...prev,
      permissions: MODULE_PERMISSION_FIELDS.reduce(
        (result, key) => {
          result[key] = false;
          return result;
        },
        {}
      ),
    }));

    setPermissionLevels(isSuperadmin ? [] : ["view"]);
  };

  return (
    <div className="layout">
      <Sidebar />

      <div className="main">
        <Header />

        <div className="add-staff-page">
          <form
            className="staff-form"
            onSubmit={handleSubmit}
          >

            <div className="form-section">
              <div className="section-title">
                Staff Photo
              </div>

              <div className="form-grid">
                <div className="form-group">
                  <label>
                    Photo
                  </label>

                  <input
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    onChange={
                      handlePhotoChange
                    }
                    disabled={
                      photoUploading
                    }
                  />

                  {photoUploading && (
                    <span className="file-uploading">
                      Uploading…
                    </span>
                  )}

                  {photoPreview &&
                    !photoUploading && (
                      <img
                        src={photoPreview}
                        alt="Staff preview"
                        style={{
                          marginTop: 8,
                          width: 80,
                          height: 80,
                          borderRadius:
                            "50%",
                          objectFit:
                            "cover",
                          display:
                            "block",
                        }}
                      />
                    )}
                </div>
              </div>
            </div>

            <div className="form-section">
              <div className="section-title">
                Basic Information
              </div>

              <div className="form-grid">

                <div className="form-group">
                  <label>
                    Full Name{" "}
                    <span className="req">
                      *
                    </span>
                  </label>

                  <input
                    type="text"
                    name="full_name"
                    value={
                      staff.full_name
                    }
                    onChange={
                      handleChange
                    }
                    required
                    placeholder="e.g. Rajesh Kumar"
                    autoComplete="name"
                  />
                </div>

                {isEdit && (
                  <div className="form-group">
                    <label>
                      Staff Code
                    </label>

                    <input
                      type="text"
                      value={staffCode}
                      readOnly
                      disabled
                      className="readonly-field"
                      autoComplete="off"
                    />
                  </div>
                )}

                <div className="form-group">
                  <label>
                    Designation
                  </label>

                  <select
                    name="designation"
                    value={
                      staff.designation
                    }
                    onChange={
                      handleChange
                    }
                  >
                    <option value="">
                      -- Select Designation --
                    </option>

                    {DESIGNATIONS.map(
                      (designation) => (
                        <option
                          key={designation}
                          value={
                            designation
                          }
                        >
                          {designation}
                        </option>
                      )
                    )}
                  </select>
                </div>

                <div className="form-group">
                  <label>
                    Email
                  </label>

                  <input
                    type="email"
                    name="email"
                    value={staff.email}
                    onChange={handleChange}
                    placeholder="email@company.com"
                    autoComplete="email"
                  />
                </div>

                <div className="form-group">
                  <label>
                    Role{" "}
                    <span className="req">
                      *
                    </span>
                  </label>

                  <select
                    name="role_id"
                    value={staff.role_id}
                    onChange={handleChange}
                    required
                  >
                    <option value="">
                      -- Select Role --
                    </option>

                    {roles.map((role) => (
                      <option
                        key={role.id}
                        value={role.id}
                      >
                        {role.role_name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group form-group--full mobile-verification-group">
                  <label>
                    Mobile Number{" "}
                    <span className="req">
                      *
                    </span>
                  </label>

                  <div className="mobile-verification-row">
                    <div className="mobile-input-wrap">
                      <span className="mobile-prefix">+91</span>
                      <input
                        type="text"
                        name="mobile"
                        maxLength={10}
                        value={staff.mobile}
                        onChange={handleChange}
                        required
                        placeholder="Enter 10-digit mobile number"
                        autoComplete="tel"
                        inputMode="numeric"
                      />
                    </div>

                    {(!isEdit || String(staff.mobile || "").replace(/\D/g, "") !== String(originalMobile || "").replace(/\D/g, "")) && (
                      <button
                        type="button"
                        className={`send-otp-btn ${otpVerified ? "send-otp-btn--verified" : ""}`}
                        onClick={handleSendOtp}
                        disabled={sendingOtp || otpVerified}
                      >
                        <span className="otp-btn-icon">
                          {otpVerified ? "✓" : "↗"}
                        </span>
                        {otpVerified
                          ? "Verified"
                          : sendingOtp
                          ? "Sending..."
                          : otpSent
                          ? "Resend OTP"
                          : "Send OTP"}
                      </button>
                    )}

                    {isEdit && String(staff.mobile || "").replace(/\D/g, "") === String(originalMobile || "").replace(/\D/g, "") && otpVerified && (
                      <span className="mobile-verified-badge">
                        <span>✓</span>
                        Verified
                      </span>
                    )}
                  </div>

                  <div className="mobile-helper">
                    {otpVerified
                      ? "This mobile number has been verified."
                      : otpSent
                      ? "OTP sent. Enter the OTP from the verification popup."
                      : "OTP verification is required before creating staff."}
                  </div>
                </div>

              </div>
            </div>

            <div className="form-section">
              <div className="section-title">
                Location Details
              </div>

              <div className="form-grid">

                <div className="form-group">
                  <label>
                    Service Location
                  </label>

                  <select
                    name="service_location_id"
                    value={
                      staff.service_location_id
                    }
                    onChange={
                      handleChange
                    }
                    disabled={
                      serviceLocations.length ===
                      0
                    }
                  >
                    <option value="">
                      -- Select Service Location --
                    </option>

                    {serviceLocations.map(
                      (loc) => (
                        <option
                          key={loc.id}
                          value={loc.id}
                        >
                          {
                            loc.location_name
                          }
                        </option>
                      )
                    )}
                  </select>

                  {serviceLocations.length ===
                    0 && (
                    <span className="field-hint">
                      No service locations
                      available.
                    </span>
                  )}
                </div>

                <div className="form-group">
                  <label>
                    Branch
                  </label>

                  <select
                    name="branch_id"
                    value={
                      staff.branch_id
                    }
                    onChange={
                      handleChange
                    }
                  >
                    <option value="">
                      -- Select Branch --
                    </option>

                    {branches.map(
                      (branch) => (
                        <option
                          key={branch.id}
                          value={branch.id}
                        >
                          {
                            branch.branch_name
                          }
                        </option>
                      )
                    )}
                  </select>
                </div>

                <div className="form-group form-group--full">
                  <label>
                    Address
                  </label>

                  <input
                    type="text"
                    name="address"
                    value={
                      staff.address
                    }
                    onChange={
                      handleChange
                    }
                    placeholder="Street / Area"
                    autoComplete="street-address"
                  />
                </div>

                <div className="form-group">
                  <label>
                    City
                  </label>

                  <input
                    type="text"
                    name="city"
                    value={staff.city}
                    onChange={
                      handleChange
                    }
                    placeholder="City name"
                    autoComplete="address-level2"
                  />
                </div>

                <div className="form-group">
                  <label>
                    Pincode
                  </label>

                  <input
                    type="text"
                    name="pincode"
                    value={
                      staff.pincode
                    }
                    maxLength={6}
                    onChange={
                      handleChange
                    }
                    placeholder="6-digit pincode"
                    autoComplete="postal-code"
                  />
                </div>

              </div>
            </div>

            {!isEdit && (
              <div className="form-section">
                <div className="section-title">
                  Security
                </div>

                <div className="form-grid">

                  <div className="form-group">
                    <label>
                      Password{" "}
                      <span className="req">
                        *
                      </span>
                    </label>

                    <input
                      type="password"
                      name="password"
                      value={
                        staff.password
                      }
                      onChange={
                        handleChange
                      }
                      required
                      minLength={6}
                      placeholder="Min. 6 characters"
                      autoComplete="new-password"
                    />
                  </div>

                  <div className="form-group">
                    <label>
                      Confirm Password{" "}
                      <span className="req">
                        *
                      </span>
                    </label>

                    <input
                      type="password"
                      name="confirm_password"
                      value={
                        staff.confirm_password
                      }
                      onChange={
                        handleChange
                      }
                      required
                      minLength={6}
                      placeholder="Repeat password"
                      autoComplete="new-password"
                    />
                  </div>

                </div>
              </div>
            )}

            {(isSuperadmin || isAdmin) && (
              <div className="form-section">
                <div
                  className="section-title"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 12,
                  }}
                >
                  <span>Permissions</span>
                  <div style={{ display: "flex", gap: 8 }}>
                    <button
                      type="button"
                      onClick={handleSelectAllPermissions}
                      style={{
                        border: "none",
                        background: "#2563eb",
                        color: "#fff",
                        padding: "6px 12px",
                        borderRadius: 6,
                        cursor: "pointer",
                        fontSize: 12,
                      }}
                    >
                      Select All
                    </button>
                    <button
                      type="button"
                      onClick={handleClearAllPermissions}
                      style={{
                        border: "1px solid #d1d5db",
                        background: "#fff",
                        color: "#374151",
                        padding: "6px 12px",
                        borderRadius: 6,
                        cursor: "pointer",
                        fontSize: 12,
                      }}
                    >
                      Clear All
                    </button>
                  </div>
                </div>

                {isSuperadmin && (
                  <div
                    className="permission-group"
                    style={{ marginBottom: 18 }}
                  >
                    <div className="permission-group-title">Access Level</div>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 22, alignItems: "center" }}>
                      {ACCESS_LEVELS.map((level) => (
                        <label key={level} className="permission-item" style={{ display: "flex", alignItems: "center", gap: 8 }}>
                          <input
                            type="checkbox"
                            checked={permissionLevels.includes(level)}
                            onChange={(e) => {
                              setPermissionLevels((prev) => {
                                if (e.target.checked) return [...new Set([...prev, level])];
                                return prev.filter((item) => item !== level);
                              });
                            }}
                          />
                          <span>{level.charAt(0).toUpperCase() + level.slice(1)}</span>
                        </label>
                      ))}
                    </div>
                    <div style={{ marginTop: 8, color: "#64748b", fontSize: 12 }}>
                      View, Edit and Delete are independent.
                    </div>
                  </div>
                )}

                {isAdmin && (
                  <div
                    style={{
                      marginBottom: 16,
                      padding: "8px 10px",
                      borderRadius: 6,
                      background: "#f8fafc",
                      color: "#64748b",
                      fontSize: 12,
                    }}
                  >
                    Admin can assign module access to Staff. Staff users always receive View access.
                  </div>
                )}

                <div className="permission-group">
                  <div className="permission-group-title">Module Access</div>
                  <div className="permission-grid">
                    {MODULE_PERMISSION_FIELDS.map((key) => (
                      <label key={key} className="permission-item">
                        <input
                          type="checkbox"
                          name={key}
                          checked={Boolean(staff.permissions[key])}
                          onChange={handleChange}
                        />
                        <span>{PERMISSION_LABELS[key]}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>
            )}

            <div className="form-section">
              <div className="section-title">
                Status
              </div>

              <div className="status-options">

                {STATUS_OPTIONS.map(
                  (status) => (
                    <label
                      key={status}
                      className={`status-option ${
                        staff.status ===
                        status
                          ? "status-option--selected"
                          : ""
                      }`}
                    >
                      <input
                        type="radio"
                        name="status"
                        value={status}
                        checked={
                          staff.status ===
                          status
                        }
                        onChange={
                          handleChange
                        }
                      />

                      <span
                        className={`status-dot-sm status-dot-sm--${status
                          .toLowerCase()
                          .replace(
                            " ",
                            "-"
                          )}`}
                      />

                      {status}
                    </label>
                  )
                )}

              </div>
            </div>

    
            <div className="button-row">

              <button
                type="button"
                className="cancel-btn"
                onClick={
                  handleBack
                }
                disabled={loading}
              >
                Cancel
              </button>

              <button
                type="submit"
                className="save-btn"
                disabled={
                  loading ||
                  photoUploading ||
                  (!isEdit && !otpVerified) ||
                  (isEdit && String(staff.mobile || "").replace(/\D/g, "") !== String(originalMobile || "").replace(/\D/g, "") && !otpVerified)
                }
              >
                {loading
                  ? "Saving…"
                  : isEdit
                  ? "Update Staff"
                  : "Save Staff"}
              </button>

            </div>

          </form>

          {otpModalOpen && !otpVerified && (
            <div
              className="otp-modal-overlay"
              role="dialog"
              aria-modal="true"
              aria-labelledby="otp-modal-title"
            >
              <div className="otp-modal">
                <div className="otp-modal-top">
                  <div className="otp-modal-icon">✦</div>

                  <button
                    type="button"
                    className="otp-modal-close"
                    onClick={() => setOtpModalOpen(false)}
                    aria-label="Close OTP verification"
                  >
                    ×
                  </button>
                </div>

                <div className="otp-modal-content">
                  <div className="otp-modal-eyebrow">
                    MOBILE VERIFICATION
                  </div>

                  <h2 id="otp-modal-title">
                    Verify your mobile number
                  </h2>

                  <p className="otp-modal-description">
                    Enter the 6-digit OTP sent to your mobile number.
                  </p>

                  <div className="otp-mobile-display">
                    <span>+91</span>
                    {staff.mobile}
                  </div>

                  <label className="otp-modal-label">
                    ENTER OTP
                  </label>

                  <input
                    className="otp-modal-input"
                    type="text"
                    value={otp}
                    onChange={(e) =>
                      setOtp(
                        e.target.value
                          .replace(/\D/g, "")
                          .slice(0, 6)
                      )
                    }
                    maxLength={6}
                    placeholder="••••••"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    autoFocus
                    onKeyDown={(e) => {
                      if (
                        e.key === "Enter" &&
                        otp.length === 6 &&
                        !verifyingOtp
                      ) {
                        handleVerifyOtp();
                      }
                    }}
                  />

                  <div className="otp-modal-actions">
                    <button
                      type="button"
                      className="otp-resend-btn"
                      onClick={handleSendOtp}
                      disabled={sendingOtp}
                    >
                      {sendingOtp ? "Sending..." : "Resend OTP"}
                    </button>

                    <button
                      type="button"
                      className="otp-verify-main-btn"
                      onClick={handleVerifyOtp}
                      disabled={verifyingOtp || otp.length !== 6}
                    >
                      {verifyingOtp
                        ? "Verifying..."
                        : "Verify OTP"}
                    </button>
                  </div>

                  <div className="otp-modal-note">
                    <span>🔒</span>
                    <span>Never share your OTP with anyone.</span>
                  </div>
                </div>
              </div>
            </div>
          )}

        </div>
      </div>
    </div>
  );
}
